import { useTranslation } from 'react-i18next'

import { useLinkLikeClick, useNotification } from '../../../../../../../hooks'
import { ConfirmationModal } from '../../../../../../Confirmation'
import {
  getListingMissingPublishItems,
  getListingPortalLabel,
  getListingPublishBlockReason,
  useRentalPortalListingPortalConnections,
} from '../../../../../../RentalPortalListing'
import { useListingProfileContext } from '../../../RentalPortalListingProfile.hooks'
import { ListingPublicationErrorModal } from '../../ListingPublicationErrorModal'
import { ListingPublicationScheduleModal } from '../../ListingPublicationScheduleModal'
import {
  LISTING_PUBLICATION_ACTION,
  LISTING_PUBLICATION_ACTION_CONFIRMATION_KEY,
  LISTING_PUBLICATION_ACTION_LABEL_KEY,
  LISTING_PUBLICATION_SCHEDULE_MODAL_PROPS,
} from '../../RentalPortalListingProfilePublishings.constants'

import { performListingPublicationAction } from './useListingProfilePublicationActions.api'
import {
  getListingPortalConnections,
  getListingPublicationActionErrorNotification,
  getListingPublicationActionNotificationKey,
  getListingPublicationActions,
  getListingPublicationScheduleBounds,
  getListingPublicationScheduleInitialValue,
} from './useListingProfilePublicationActions.utils'

export function useListingProfilePublicationActions() {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const { handleCloseModal, handleEntityReload, listingData, setModal } =
    useListingProfileContext()

  const { hasPortalsError, isLoadingPortals, portals } =
    useRentalPortalListingPortalConnections()

  function getActions(publication) {
    return getListingPublicationActions({
      listing: listingData,
      portalConnections: getListingPortalConnections({
        hasPortalsError,
        portals,
      }),
      publication,
      readinessBlockReason:
        getListingPublishBlockReason({
          missingItems: getListingMissingPublishItems({
            listing: listingData,
            t,
          }),
          t,
        }) ?? null,
      t,
    })
  }

  async function performAction({ action, publication, scheduledAt }) {
    return performListingPublicationAction({
      action,
      listingId: listingData.id,
      publication,
      scheduledAt,
    }).then(() => {
      handleCloseModal()

      handleAddSuccessNotification(
        getListingPublicationActionNotificationKey({ action, publication })
      )

      return handleEntityReload()
    })
  }

  function getHandleSubmitSchedule({ action, publication }) {
    return async scheduledAt =>
      performAction({ action, publication, scheduledAt })
  }

  function getHandleSubmitConfirmation({ action, publication }) {
    return async () =>
      performAction({ action, publication }).catch(error => {
        handleCloseModal()

        handleAddErrorNotification(
          getListingPublicationActionErrorNotification({
            error,
            t,
            onRetry: () => setItemAction(action, publication),
          })
        )
      })
  }

  function setItemAction(action, publication) {
    switch (action) {
      case LISTING_PUBLICATION_ACTION.openAd:
        handleLinkClick({ link: publication.public_url, outsideLink: true })
        break
      case LISTING_PUBLICATION_ACTION.details:
        setModal(
          <ListingPublicationErrorModal
            publication={publication}
            onClose={handleCloseModal}
          />
        )
        break
      case LISTING_PUBLICATION_ACTION.editDeactivation:
      case LISTING_PUBLICATION_ACTION.editSchedule:
        setModal(
          <ListingPublicationScheduleModal
            {...LISTING_PUBLICATION_SCHEDULE_MODAL_PROPS[action]}
            bounds={getListingPublicationScheduleBounds({
              action,
              publication,
            })}
            initialValue={getListingPublicationScheduleInitialValue({
              action,
              publication,
            })}
            onClose={handleCloseModal}
            onSubmit={getHandleSubmitSchedule({ action, publication })}
          />
        )
        break
      default:
        setModal(
          <ConfirmationModal
            actionButtonsProps={{
              submitText: t(LISTING_PUBLICATION_ACTION_LABEL_KEY[action]),
            }}
            description={t(
              LISTING_PUBLICATION_ACTION_CONFIRMATION_KEY[action],
              { portal: getListingPortalLabel(publication.portal) }
            )}
            onCancel={handleCloseModal}
            onSubmit={getHandleSubmitConfirmation({ action, publication })}
          />
        )
    }
  }

  return { getActions, isLoadingPortals, portals, setItemAction }
}
